import React, { useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { conditions, machiningFields, textFields } from './machiningFields';
import { TechnicalSpecifications } from './TechnicalSpecifications';

export function AssetForm({
    kind,
    asset,
    categories,
    company,
    saving: assetSaving,
    error,
    onClose,
    onSave,
    onDocumentError,
    readOnly = false,
}) {
    const documents = useRef(null);
    const [documentsBusy, setDocumentsBusy] = useState(false);
    const saving = assetSaving || documentsBusy;
    const definition = machiningFields[kind];
    const [values, setValues] = useState(() => {
        const initial = {};
        [
            definition.number,
            'category',
            'assetCondition',
            'maintenanceRequirement',
            'manufactureYear',
            'commissionedOn',
            'warrantyExpiresOn',
            'remarks',
            ...textFields.map(([field]) => field),
        ].forEach((field) => {
            initial[field] = asset?.[field] == null ? '' : String(asset[field]);
        });

        return initial;
    });
    const [validation, setValidation] = useState({});
    const errors = { ...validation, ...(error?.fields || {}) };
    const change = (field) => (event) => {
        setValues((previous) => ({ ...previous, [field]: event.target.value }));
        setValidation((previous) => ({ ...previous, [field]: '' }));
    };
    const submit = async (event) => {
        event.preventDefault();
        if (readOnly) return;
        const fields = {};
        const labels = {
            [definition.number]: definition.numberLabel,
            category: 'Category',
            assetCondition: 'Condition',
            ...Object.fromEntries(textFields.map(([field, label]) => [field, label])),
        };

        definition.required.forEach((field) => {
            if (values[field].trim() === '')
                fields[field] =
                    field === 'maintenanceRequirement'
                        ? 'Choose Yes or No.'
                        : `${labels[field]} is required.`;
        });

        if (values.manufactureYear.trim() && !/^[1-9]\d{3}$/.test(values.manufactureYear.trim()))
            fields.manufactureYear = 'Use a four-digit year from 1000 to 9999.';

        setValidation(fields);

        if (Object.keys(fields).length) return;

        const normalized = Object.fromEntries(
            Object.entries(values).map(([field, value]) => [field, value.trim() || null])
        );

        normalized.category = values.category;
        normalized.assetCondition = Number(values.assetCondition);
        normalized.maintenanceRequirement = values.maintenanceRequirement === 'true';
        normalized.manufactureYear =
            normalized.manufactureYear === null ? null : Number(normalized.manufactureYear);
        const hasDocuments = documents.current?.hasPending();
        const saved = await onSave(normalized, hasDocuments);

        if (saved && hasDocuments && (await documents.current?.uploadPending(saved.id))) onClose();
    };

    const control = (field, label, input) => (
        <Form.Group controlId={`asset-${field}`} className="mb-3">
            <Form.Label>
                {label}
                {definition.required.includes(field) ? ' *' : ''}
            </Form.Label>
            {input}
            {errors[field] ? (
                <div className="invalid-feedback d-block" role="alert">
                    {errors[field]}
                </div>
            ) : null}
        </Form.Group>
    );

    const input = (field, label, maxLength, type = 'text') =>
        control(
            field,
            label,
            <Form.Control
                type={type}
                value={values[field]}
                onChange={change(field)}
                maxLength={maxLength}
                isInvalid={!!errors[field]}
                disabled={saving || readOnly}
            />
        );
        
    return (
        <Modal
            show
            onHide={saving ? undefined : onClose}
            size="lg"
            backdrop={saving ? 'static' : true}
            keyboard={!saving}
            aria-labelledby="asset-form-title"
        >
            <Form onSubmit={submit} noValidate>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title id="asset-form-title">
                        {readOnly ? 'View' : asset ? 'Edit' : 'Add'} {definition.singular}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error?.message ? <Alert variant="danger">{error.message}</Alert> : null}
                    <div className="mb-3">
                        <span id="asset-company-label">Company</span>
                        <p aria-labelledby="asset-company-label" className="form-control-plaintext">
                            {company}
                        </p>
                    </div>
                    <Row>
                        <Col md={6}>{input(definition.number, definition.numberLabel, 20)}</Col>
                        <Col md={6}>
                            {control(
                                'category',
                                'Category',
                                <Form.Select
                                    value={values.category}
                                    disabled={saving || readOnly}
                                    isInvalid={!!errors.category}
                                    onChange={change('category')}
                                >
                                    <option value="">Choose category</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={String(category.category)}>
                                            {category.description}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Col>
                        {textFields.map(([field, label, max]) => (
                            <Col key={field} md={6}>
                                {input(field, label, max)}
                            </Col>
                        ))}
                        <Col md={6}>
                            {control(
                                'assetCondition',
                                'Condition',
                                <Form.Select
                                    value={values.assetCondition}
                                    disabled={saving || readOnly}
                                    isInvalid={!!errors.assetCondition}
                                    onChange={change('assetCondition')}
                                >
                                    <option value="">Choose condition</option>
                                    {conditions.map((label, value) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Col>
                        <Col md={6}>
                            {control(
                                'maintenanceRequirement',
                                'Maintenance required',
                                <Form.Select
                                    value={values.maintenanceRequirement}
                                    disabled={saving || readOnly}
                                    isInvalid={!!errors.maintenanceRequirement}
                                    onChange={change('maintenanceRequirement')}
                                >
                                    <option value="">Choose Yes or No</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </Form.Select>
                            )}
                        </Col>
                        <Col md={6}>{input('manufactureYear', 'Manufacture year')}</Col>
                        <Col md={6}>
                            {input('commissionedOn', 'Commissioned on', undefined, 'date')}
                        </Col>
                        <Col md={6}>
                            {input('warrantyExpiresOn', 'Warranty expires on', undefined, 'date')}
                        </Col>
                        <Col xs={12}>
                            {control(
                                'remarks',
                                'Remarks',
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={values.remarks}
                                    onChange={change('remarks')}
                                    disabled={saving || readOnly}
                                />
                            )}
                        </Col>
                    </Row>
                    <TechnicalSpecifications
                        ref={documents}
                        kind={kind}
                        asset={asset}
                        disabled={assetSaving}
                        readOnly={readOnly}
                        onBusy={setDocumentsBusy}
                        onError={onDocumentError}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" disabled={saving} onClick={onClose}>
                        Cancel
                    </Button>
                    {!readOnly && <Button type="submit" disabled={saving || categories.length === 0}>
                        {saving ? 'Saving…' : `Save ${definition.singular}`}
                    </Button>}
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
